import React from 'react';
import { Key, X, Loader2 } from 'lucide-react';
import { User } from '../../../services/api';

interface PasswordChangeModalProps {
  user: User;
  newPassword: string;
  confirmPassword: string;
  error: string | null;
  loading: boolean;
  onClose: () => void;
  onPasswordChange: () => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
}

export function PasswordChangeModal({
  user,
  newPassword,
  confirmPassword,
  error,
  loading,
  onClose,
  onPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
}: PasswordChangeModalProps): React.JSX.Element {
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" 
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-4" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-600/30">
              <Key className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-amber-50 dark:text-amber-50 text-zinc-900">Change Password</h3>
              <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600">{user.name}</p>
              {user.email && (
                <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-500 mt-1">{user.email}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-white dark:hover:text-white hover:text-zinc-900 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 dark:bg-red-500/10 bg-red-50 border border-red-500/30 dark:border-red-500/30 border-red-300/30 rounded-lg">
            <p className="text-red-400 dark:text-red-400 text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => onNewPasswordChange(e.target.value)}
              placeholder="Enter new password"
              className="w-full px-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => onConfirmPasswordChange(e.target.value)}
              placeholder="Confirm new password"
              className="w-full px-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={onPasswordChange}
              disabled={loading || !newPassword || newPassword !== confirmPassword}
              className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-4 py-3 rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-2 shadow-lg shadow-amber-600/30"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Changing...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4" />
                  Change Password
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-3 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

